import { useState, useRef, useEffect } from 'react';
import { aiMessages, agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

type Message = {
  id: number;
  role: 'assistant' | 'user';
  text: string;
};

const suggestions = [
  'Summarize today\'s leads',
  'Draft a quote for honeymoon package',
  'List overdue payments',
  'Upcoming departures this week',
];

const autoReplies: Record<string, string> = {
  'summarize': 'You have 12 new leads today. Top sources: WhatsApp (6), Website Forms (4), Referral (2). Highest budget lead: Carlos Reyes — AED 48,000 for USA East Coast tour.',
  'quote': 'Here\'s a draft quote for a Honeymoon Package:\n\n🌴 Maldives Overwater Villa — 7 nights\n✈ Business Class Flights (DXB ↔ MLE)\n🚤 Speedboat transfers\n🍽 Full-board dining\n\nTotal: AED 18,500 per couple\n\nShall I create a formal invoice?',
  'overdue': 'Found 7 overdue payments totaling AED 42,800:\n• BK-1080 Carlos Reyes — AED 48,000 (14 days overdue)\n• BK-1079 Sophie Martin — AED 32,000 (refund pending)\n• BK-1075 Ali Hassan — AED 8,500 (7 days overdue)\n\nShall I send reminder emails?',
  'departure': 'Upcoming departures this week:\n• 15 Apr — Ahmed Al-Rashid (Maldives) ✅ Docs complete\n• 17 Apr — Emma Williams (Cruise) ⚠ Visa pending\n• 18 Apr — Priya Nair (Thailand) ✅ Docs complete\n\nWould you like me to send confirmation messages?',
};

function getBotReply(text: string): string {
  const lower = text.toLowerCase();
  for (const [key, reply] of Object.entries(autoReplies)) {
    if (lower.includes(key)) return reply;
  }
  return 'Got it! I\'m processing your request. For advanced AI automation, connect the OpenAI integration in Settings → AI Assistant.';
}

export default function AIChat() {
  const [messages, setMessages] = useState<Message[]>(aiMessages as Message[]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { id: Date.now(), role: 'user', text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setTyping(true);

    setTimeout(() => {
      const reply: Message = {
        id: Date.now() + 1,
        role: 'assistant',
        text: getBotReply(text),
      };
      setMessages((prev) => [...prev, reply]);
      setTyping(false);
    }, 1200);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 flex flex-col" style={{ height: '420px' }}>
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-gray-50">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${brand}15` }}
        >
          <i className="ri-robot-2-line text-base" style={{ color: brand }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">AI Assistant</p>
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
            <span className="text-xs text-gray-400">Online</span>
          </div>
        </div>
        <span
          className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ backgroundColor: `${brand}12`, color: brand }}
        >
          GPT-4
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
          >
            {msg.role === 'assistant' && (
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ backgroundColor: `${brand}15` }}
              >
                <i className="ri-robot-2-line text-xs" style={{ color: brand }} />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                msg.role === 'user'
                  ? 'text-white rounded-tr-sm'
                  : 'bg-gray-50 text-gray-700 rounded-tl-sm'
              }`}
              style={msg.role === 'user' ? { backgroundColor: brand } : {}}
            >
              {msg.text}
            </div>
          </div>
        ))}

        {typing && (
          <div className="flex gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${brand}15` }}
            >
              <i className="ri-robot-2-line text-xs" style={{ color: brand }} />
            </div>
            <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Quick suggestions */}
      <div className="px-4 py-2 flex gap-1.5 overflow-x-auto">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => sendMessage(s)}
            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-full border border-gray-200 text-gray-500 hover:border-teal-300 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            {s}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="px-4 pb-4">
        <div className="flex gap-2 bg-gray-50 rounded-2xl px-3 py-2 border border-gray-100 focus-within:border-teal-200 transition-colors">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage(input)}
            placeholder="Ask anything about your business..."
            className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim()}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-white cursor-pointer disabled:opacity-40 flex-shrink-0 transition-opacity"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-send-plane-2-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}
